// MODULES
import { LotI, NFTI } from '@tacans/shared-marketplace-layer';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo } from 'react';
import { useAvailableCopies } from 'src/features/common/collectible/hooks/use-available-copies';
import { useGetUniverseTemplate } from 'src/hooks/use-get-universe-template';
// RESOURCES
import { Button } from 'src/ui/common/atoms/button';
import { ButtonProps } from 'src/ui/common/atoms/button/button.types';
import { useGetActiveTokenTransaction } from '../../hooks/useGetActiveTokenTransaction.hook';
import { ChangePriceModal } from '../../modals/change-price-modal';
import { TChangePriceModal } from '../../modals/change-price-modal/change-price-modal.types';
import { useActionWithAuthorization } from './actions.hooks';

interface IChangePrice {
    lot: LotI;
    nft: NFTI;
    small?: boolean;
    type: TChangePriceModal;
    variant?: ButtonProps['variant'];
}

export const ChangePriceAction = ({ lot, nft, small, type, variant = 'gradient' }: IChangePrice) => {
    const { t } = useTranslation(['actions', 'pages']);
    const availableCopies = useAvailableCopies();
    const activeTransactions = useGetActiveTokenTransaction();
    const { isNinjaTemplate, isOceanTemplate, isSyndicate } = useGetUniverseTemplate();

    const locale = useMemo(
        () => ({
            sell: t('trade.listForSale', { ns: 'actions' }),
            changePrice: t('trade.changePrice', { ns: 'actions' }),
            notEnoughCopiesToSell: t('nft.view.lots.notEnoughCopiesToSell', { ns: 'pages', copies: availableCopies }),
        }),
        [availableCopies, t]
    );
    const title = type === 'CHANGE_PRICE' ? locale.changePrice : locale.sell;

    const sellLotCallback = useCallback(() => {
        ChangePriceModal.open({
            nft,
            lot,
            type,
        });
    }, [nft, lot, type]);

    const actionWithAuthorization = useActionWithAuthorization({
        callback: sellLotCallback,
    });

    const isNotEnoughCopiesToSell = type === 'SELL' && availableCopies < +lot.copies!;

    const getButtonVariant = useCallback(() => {
        if (isNinjaTemplate) {
            return 'roundedInverse';
        }
        if (isOceanTemplate) {
            return 'o_blue';
        }
        if (isSyndicate) {
            return 'white';
        }
        return variant;
    }, [isNinjaTemplate, isOceanTemplate, variant]);

    return (
        <Button
            variant={getButtonVariant()}
            onClick={actionWithAuthorization}
            size={small ? 'sm' : 'lg'}
            disabled={isNotEnoughCopiesToSell || !!activeTransactions[0]}
            data-test-id="sell-lot-btn"
        >
            {title}
        </Button>
    );
};